import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Lock, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Link non valido."); return; }
    if (password.length < 6) { toast.error("La password deve avere almeno 6 caratteri."); return; }
    if (password !== confirm) { toast.error("Le password non coincidono."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setUser(data.user);
      toast.success("Password aggiornata. Bentornato!");
      navigate(data.user?.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050A15] px-6 py-16 text-white">
      <div className="mx-auto max-w-md">
        <Logo />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Scegli una nuova password</p>
          <h1 className="mt-4 font-serif text-4xl font-light">Reimposta password</h1>
          <p className="mt-3 text-white/60">Almeno 6 caratteri. Ti collegheremo subito al tuo account.</p>
          {!token && (
            <p className="mt-6 rounded-xl border border-red-400/30 bg-red-400/10 p-4 text-sm text-red-200">
              Link non valido: manca il token. Torna al login e richiedi un nuovo link.
            </p>
          )}
          <form onSubmit={submit} className="mt-8 space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/40">Nuova password</Label>
              <div className="relative mt-2">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required minLength={6} placeholder="Almeno 6 caratteri"
                  data-testid="reset-password-input"
                  className="border-white/10 bg-white/5 pl-10 text-white placeholder:text-white/25" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/40">Ripeti password</Label>
              <div className="relative mt-2">
                <Check className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required minLength={6} placeholder="Ripeti la password"
                  data-testid="reset-confirm-input"
                  className="border-white/10 bg-white/5 pl-10 text-white placeholder:text-white/25" />
              </div>
            </div>
            <Button type="submit" disabled={busy || !token} data-testid="reset-submit"
              className="w-full gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
              {busy ? "Salvataggio..." : "Aggiorna password"} <ArrowRight className="h-4 w-4" />
            </Button>
          </form>
          <p className="mt-8 text-sm text-white/50">
            <Link to="/login" className="text-[#D4AF37] hover:underline">Torna al login</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
